import { type Chat, type ChatMessage, type Content, safeImageUrl } from "@rhema/client";
import { Card, FileCard, PlayIcon } from "@rhema/ui";
import { lazy, Suspense, useState } from "react";
import { fileNameOf, openAs } from "../media/files";
import { extensionOf, formatBytes, formatDuration, saveBlob } from "../media/prepare";
import { useMediaUrl } from "../media/useMedia";
import { fileEntry, type ViewerEntry, viewerEntries } from "../media/viewerEntries";

const MediaViewer = lazy(() => import("../media/MediaViewer"));

type Media = Extract<Content, { type: "media" }>;
type Item = { message: ChatMessage; content: Media };

const SHOWN = 30;

/** Photos, videos and files in the messages this device has decrypted, newest first. */
export function galleryOf(messages: ChatMessage[]): { visual: Item[]; files: Item[] } {
  const items = messages
    .filter((m) => !m.deleted && m.content?.type === "media" && m.content.media.key)
    .map((m) => ({ message: m, content: m.content as Media }))
    .reverse();
  return {
    visual: items.filter((i) => i.content.kind === "photo" || i.content.kind === "video"),
    files: items.filter((i) => i.content.kind === "file"),
  };
}

function Tile({ item, onOpen }: { item: Item; onOpen: () => void }) {
  const { media, kind, caption } = item.content;
  const { url } = useMediaUrl(media, undefined, kind === "photo");
  // Only an embedded image, so a preview can never call out to another server.
  const thumbnail = safeImageUrl(media.thumbnail);
  return (
    <button
      type="button"
      aria-label={kind === "photo" ? caption || "Photo" : `Video, ${formatDuration(media.duration)}`}
      onClick={onOpen}
      className="relative aspect-square w-full cursor-pointer overflow-hidden rounded-tile border-0 bg-surface-header p-0"
    >
      {thumbnail && (
        <img
          src={thumbnail}
          alt=""
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-sm"
        />
      )}
      {url && <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      {kind === "video" && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue text-white">
            <PlayIcon size={12} />
          </span>
        </span>
      )}
    </button>
  );
}

function FileRow({ item, onOpen }: { item: Item; onOpen?: () => void }) {
  const { media } = item.content;
  const { load, error, loading } = useMediaUrl(media, undefined, false);
  const name = media.name || "File";
  return (
    <li>
      <FileCard
        name={name}
        kind={extensionOf(media.name, media.mime)}
        detail={error ?? (loading ? "Downloading" : formatBytes(media.size))}
        onOpen={onOpen}
        onDownload={() => void load().then((blob) => blob && saveBlob(blob, fileNameOf(media)))}
      />
    </li>
  );
}

// The "Media and files" card in chat info.
export function MediaGallery({
  chat,
  messages,
  meId,
}: {
  chat: Chat;
  messages: ChatMessage[];
  meId?: string;
}) {
  const [viewer, setViewer] = useState<{ entries: ViewerEntry[]; start: number } | null>(null);
  const { visual, files } = galleryOf(messages);
  if (!visual.length && !files.length) return null;

  const nameOf = (id: string | null) =>
    chat.members.find((x) => x.user.id === id)?.user.display_name ?? "Someone";
  const open = (m: ChatMessage) => {
    const entries = viewerEntries(messages, meId, nameOf);
    const start = entries.findIndex((e) => e.id === m.id);
    if (start >= 0) setViewer({ entries, start });
  };
  const openFile = (m: ChatMessage) => {
    const entry = fileEntry(m, meId, nameOf);
    if (entry) setViewer({ entries: [entry], start: 0 });
  };

  return (
    <Card className="px-4 py-3.5">
      <h2 className="m-0 text-[15px] font-bold">Media and files</h2>
      <p className="m-0 mt-0.5 text-[13px] text-ink-secondary">From the messages on this device.</p>
      {visual.length > 0 && (
        <ul aria-label="Photos and videos" className="m-0 mt-2.5 grid list-none grid-cols-3 gap-1.5 p-0">
          {visual.slice(0, SHOWN).map((item) => (
            <li key={item.message.id} className="flex">
              <Tile item={item} onOpen={() => open(item.message)} />
            </li>
          ))}
        </ul>
      )}
      {files.length > 0 && (
        <ul aria-label="Files" className="m-0 mt-2.5 flex list-none flex-col gap-2 p-0">
          {files.slice(0, SHOWN).map((item) => (
            <FileRow
              key={item.message.id}
              item={item}
              onOpen={openAs(item.content.media.mime) ? () => openFile(item.message) : undefined}
            />
          ))}
        </ul>
      )}
      {viewer && (
        <Suspense fallback={null}>
          <MediaViewer
            entries={viewer.entries}
            start={viewer.start}
            chat={chat}
            onClose={() => setViewer(null)}
          />
        </Suspense>
      )}
    </Card>
  );
}
